/* eslint-disable react/prop-types */
import React, { useEffect, useContext, useState, useRef, useCallback } from 'react';

import GridItem from 'components/Grid/GridItem.js';
import { Button } from '@mui/material';
import AuthenticatedContainer from 'components/AuthenticatedContainer';
import CircularProgress from '@mui/material/CircularProgress';
import UserService from '../../services/UserService';
import makeStyles from '@mui/styles/makeStyles';
import { Context } from 'Store';
import types from 'Reducer/types';
import useAPI from 'useAPI';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import TermsOfUseContent from './TermsOfUseContent';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import mixpanel from "mixpanel-browser"
import {
  NAV_HEIGHT,
  PAGE_TOP_PADDING,
  applyPageVisibility,
  buildPages,
  getBlockElements,
} from './paginatePolicy';

const useStyles = makeStyles(() => ({
  buttonProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
}));

function PaginatedTerms({ onAgree, onDisagree, saving }) {
  const classes = useStyles();
  const outerRef = useRef(null);
  const measureRef = useRef(null);
  const displayRef = useRef(null);
  const pagesRef = useRef([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(0);
  const [checked, setChecked] = useState(false);

  const computePages = useCallback(() => {
    const outer = outerRef.current;
    const measureEl = measureRef.current;
    if (!outer || !measureEl) return;

    const baseHeight = outer.clientHeight - NAV_HEIGHT - PAGE_TOP_PADDING;
    if (baseHeight <= 0) return;

    const policyRoot = measureEl.querySelector('.policy');
    if (!policyRoot) return;

    const blocks = getBlockElements(policyRoot);
    if (blocks.length === 0) return;

    const newPages = buildPages(blocks, baseHeight);

    pagesRef.current = newPages;
    setTotalPages(newPages.length);
    setCurrentPage(prev => Math.min(prev, Math.max(0, newPages.length - 1)));
  }, []);

  // Apply visibility when page changes
  useEffect(() => {
    if (displayRef.current && pagesRef.current.length > 0) {
      applyPageVisibility(displayRef.current, pagesRef.current[currentPage] || []);
      const needsScroll = displayRef.current.scrollHeight > displayRef.current.clientHeight;
      displayRef.current.style.overflowY = needsScroll ? 'auto' : 'hidden';
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    const timer = setTimeout(computePages, 100);
    const observer = new ResizeObserver(computePages);
    if (outerRef.current) observer.observe(outerRef.current);
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [computePages]);

  const isLastPage = totalPages > 0 && currentPage === totalPages - 1;

  return (
    <Box
      ref={outerRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: '60vh',
        position: 'relative',
      }}
    >
      {/* Hidden measurement container — same width, used to measure block heights */}
      <Box
        ref={measureRef}
        sx={{
          position: 'absolute',
          visibility: 'hidden',
          top: 0,
          left: 0,
          right: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
          zIndex: -1,
        }}
      >
        <TermsOfUseContent />
      </Box>

      {/* Visible display — blocks hidden/shown via DOM */}
      <Box ref={displayRef} sx={{ flex: 1, overflow: 'hidden', pt: `${PAGE_TOP_PADDING}px` }}>
        <TermsOfUseContent />
      </Box>

      {/* Navigation bar */}
      <Box
        sx={{
          px: 2,
          py: 1,
          borderTop: '1px solid #e0e0e0',
        }}
      >
        {/* Back / Page / Next row */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            minHeight: 40,
          }}
        >
          <Box>
            {currentPage > 0 && (
              <Button onClick={() => setCurrentPage(p => p - 1)}>
                Back
              </Button>
            )}
          </Box>

          {totalPages > 1 && (
            <Typography variant="body2" color="text.secondary">
              Page {currentPage + 1} of {totalPages}
            </Typography>
          )}

          {!isLastPage && (
            <Button onClick={() => setCurrentPage(p => p + 1)}>
              {'Next ->'}
            </Button>
          )}

          {isLastPage && <Box />}
        </Box>

        {/* Checkbox + Agree/Disagree (last page only) */}
        {isLastPage && (
          <Box sx={{ pt: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={checked}
                  onChange={(e) => setChecked(e.target.checked)}
                  color="primary"
                />
              }
              label="I have read the Terms of Use"
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
              <Button
                onClick={() => onDisagree()}
                disabled={!checked}
              >
                Disagree
              </Button>
              <div style={{ position: 'relative' }}>
                <Button
                  onClick={() => onAgree()}
                  color="primary"
                  disabled={!checked}
                  autoFocus
                >
                  Agree
                </Button>
                {saving && <CircularProgress size={14} className={classes.buttonProgress} />}
              </div>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default function TNCDialogBox() {
  const api = useAPI();
  const { state, dispatch } = useContext(Context);
  const { tncChange, user } = state;
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    UserService.init(api);
  }, [api]);

  useEffect(() => {
    if (user && !user.is_new) {
      setStep(2);
    }
  }, [user]);

  function isUserNeedToAgree() {
    return Object.keys(user).length > 0 && !user.is_agree;
  }

  function isShowRegistrationSuccessful() {
    return isUserNeedToAgree() && step === 1;
  }

  const gotoStep2 = () => {
    mixpanel.track('REGISTRATION_SUCCESSFUL');
    setStep(2);
  }

  function isShowTermsAndConditions() {
    return isUserNeedToAgree() && step === 2;
  }

  const handleAgreeToTermsAndConditions = (agree) => {
    mixpanel.track(agree ? 'TNC_AGREE' : 'TNC_DISAGREE')
    setSaving(true);
    UserService.updateTermsAndConditions(user, agree).then((user) => {
      dispatch({ type: types.SET_USER, payload: user });
      dispatch({
        type: types.SET_TNC_CHANGE,
        payload: !tncChange,
      });
    });
    setSaving(false);
  };

  return (
    <AuthenticatedContainer>
        <GridItem xs={12} sm={12} md={8} align='center'>
          {/* Step 1 - Thanks for registering */}
          <Dialog
            open={isShowRegistrationSuccessful()}
            aria-labelledby='alert-dialog-title'
            aria-describedby='alert-dialog-description'
          >
            <DialogTitle id='alert-dialog-title'>{'Registration Successful'}</DialogTitle>
            <DialogContent>
              <DialogContentText id='alert-dialog-description'>
                Thank you for registering. Please review our Terms of Use.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={gotoStep2}
                color='primary'
                autoFocus
              >
                Continue
              </Button>
            </DialogActions>
          </Dialog>

          {/* Step 2 - T&Cs (paginated) */}
          <Dialog
            open={isShowTermsAndConditions()}
            aria-labelledby='alert-dialog-title'
            aria-describedby='alert-dialog-description'
            fullWidth
            maxWidth="sm"
            PaperProps={{ sx: { height: '90vh', maxHeight: '90vh' } }}
          >
            <DialogContent style={{ padding: 0, overflow: 'hidden', display: 'flex' }}>
              <PaginatedTerms
                onAgree={() => handleAgreeToTermsAndConditions(true)}
                onDisagree={() => handleAgreeToTermsAndConditions(false)}
                saving={saving}
              />
            </DialogContent>
          </Dialog>
        </GridItem>
    </AuthenticatedContainer>
  );
}
